import { useQuery } from "@tanstack/react-query"
import { AlertCircle, Layers } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { KpiPagosPorEjecutar } from "@/componentes/kpi/KpiTesoreria"
import { FichaOrden } from "@/componentes/ordenes/FichaOrden"
import { ModalPagoCombinado } from "@/componentes/ordenes/ModalPagoCombinado"
import type { Orden } from "@/componentes/ordenes/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha, hoyLocal } from "@/lib/fechas"

/**
 * La bandeja de tesorería: órdenes aprobadas que esperan a que alguien pague.
 *
 * Es el final del circuito de las «solicitudes de pago» de `constructora_v2`: la orden
 * se redactó, se aprobó, y aquí sale el dinero. Pulsar una abre su ficha, que es donde
 * se ejecuta —la misma que en «Órdenes de pago»: dos pantallas que pagan distinto
 * serían dos formas de equivocarse—.
 *
 * ⚠ **Más antiguas primero.** Una orden aprobada es una deuda reconocida con un
 * proveedor; la que lleva más tiempo esperando es la que primero hay que mirar.
 *
 * ⚠ **Varias órdenes del MISMO proveedor y moneda se pagan JUNTAS** (19/10): se marcan y
 * «Pagar juntas» abre un solo pago —una transferencia— por todas
 * (`ModalPagoCombinado`). Con proveedores o monedas distintas el botón no se ofrece y
 * se dice por qué: una transferencia tiene un beneficiario y una moneda.
 */
export function PagosPorEjecutar() {
  const [abierta, setAbierta] = useState<number | null>(null)
  const [marcadas, setMarcadas] = useState<number[]>([])
  const [combinando, setCombinando] = useState(false)

  const ordenes = useQuery({
    queryKey: ["ordenes-pago", "APROBADA"],
    queryFn: () => api.get<Orden[]>("/api/v1/ordenes-pago?estado=APROBADA&limit=100"),
  })

  if (ordenes.isPending) return <p className="py-6">Cargando…</p>
  if (ordenes.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar los pagos pendientes.
      </p>
    )
  }

  // El servidor las da de la más nueva a la más vieja; aquí se invierte.
  const filas = [...ordenes.data].reverse()
  const elegidas = filas.filter((o) => marcadas.includes(o.id))
  const mezcla =
    elegidas.length > 1 &&
    elegidas.some(
      (o) => o.proveedor_id !== elegidas[0]?.proveedor_id || o.divisa_id !== elegidas[0]?.divisa_id,
    )
  const documento = (): DocumentoExportable => ({
    titulo: "Pagos por ejecutar",
    subtitulo: "Órdenes aprobadas pendientes de pago, de la más antigua a la más reciente",
    secciones: [
      {
        titulo: "Órdenes aprobadas pendientes de pago",
        columnas: [
          { titulo: "Orden", tipo: "texto" },
          { titulo: "Proveedor", tipo: "texto" },
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "Requerida", tipo: "fecha" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Neto", tipo: "monto" },
          { titulo: "A pagar", tipo: "monto" },
        ],
        filas: filas.map((o) => ({
          celdas: [
            o.codigo,
            o.proveedor ?? null,
            o.concepto,
            o.fecha_requerida ?? null,
            o.divisa ?? null,
            Number(o.monto_neto),
            Number(o.monto_pendiente || o.monto_neto),
          ],
        })),
      },
    ],
  })
  const marcar = (id: number) =>
    setMarcadas((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]))

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <p className="text-sm" style={{ color: mezcla ? "var(--warning)" : "var(--fg-muted)" }}>
          {mezcla
            ? "Sólo se pagan juntas órdenes del mismo proveedor y la misma moneda."
            : elegidas.length > 0
              ? `${elegidas.length} marcada${elegidas.length === 1 ? "" : "s"}`
              : "Marca dos o más órdenes del mismo proveedor para pagarlas juntas."}
        </p>
        <Boton
          variante="primario"
          disabled={elegidas.length < 2 || mezcla}
          onClick={() => setCombinando(true)}
        >
          <Layers size={16} /> Pagar juntas
        </Boton>
        {filas.length > 0 && (
          <Exportar
            titulo="Pagos por ejecutar"
            subtitulo="Órdenes aprobadas pendientes de pago, de la más antigua a la más reciente"
            documento={documento}
          />
        )}
      </div>
      {filas.length > 0 && <KpiPagosPorEjecutar ordenes={filas} hoy={hoyLocal()} />}
      <Tabla etiqueta="Órdenes aprobadas pendientes de pago">
        <Cabecera>
          <Th>Orden</Th>
          <Th>Proveedor</Th>
          <Th>Concepto</Th>
          <Th>Requerida</Th>
          <Th derecha>A pagar</Th>
        </Cabecera>
        <Cuerpo>
          {filas.length === 0 && (
            <TablaVacia colSpan={5}>No hay órdenes aprobadas esperando pago.</TablaVacia>
          )}
          {filas.map((orden) => (
            <Fila key={orden.id} className="cursor-pointer" onClick={() => setAbierta(orden.id)}>
              <Td numero>
                {/* La casilla no abre la ficha: marcar es otra cosa que mirar. */}
                <span className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={marcadas.includes(orden.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => marcar(orden.id)}
                    aria-label={`Marcar ${orden.codigo} para pagarla junto con otras`}
                  />
                  {orden.codigo}
                </span>
              </Td>
              <Td>{orden.proveedor ?? "—"}</Td>
              <Td>{orden.concepto}</Td>
              <Td numero>{comoFecha(orden.fecha_requerida) || "—"}</Td>
              <Td derecha numero>
                {/* Con un pago parcial, lo que queda: el neto invitaría a pagarlo dos veces. */}
                {monto(orden.monto_pendiente || orden.monto_neto)} {orden.divisa ?? ""}
                {orden.monto_pendiente && orden.monto_pendiente !== orden.monto_neto && (
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    resto de {monto(orden.monto_neto)}
                  </span>
                )}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
      {abierta !== null && (
        <FichaOrden ordenId={abierta} onCerrar={() => setAbierta(null)} desdeTesoreria />
      )}
      {combinando && (
        <ModalPagoCombinado
          ordenes={elegidas}
          onCerrar={() => {
            setCombinando(false)
            setMarcadas([])
          }}
        />
      )}
    </section>
  )
}
